import React, { useState, useEffect } from 'react';
import { useTheme } from '../../context/ThemeContext';
import { formatCurrency } from '../../config/currency';
import api from '../../api/api';
import {
  ChefHat,
  Clock,
  Timer,
  AlertTriangle,
  BarChart3,
  TrendingUp,
  XCircle,
  CheckCircle,
  Table2,
  ArrowUpDown,
  Flame,
  Eye
} from 'lucide-react';

interface KitchenPerformanceProps {
  vendorId: number;
  startDate: string;
  endDate: string;
}

interface KitchenPerformanceData {
  totalKots: number;
  completedKots: number;
  cancelledKots: number;
  avgPrepTimeMinutes: number;
  avgAckTimeMinutes: number;
  avgCookTimeMinutes: number;
  avgServiceTimeMinutes: number;
  kitchenBreakdown: {
    kitchenId: number;
    kitchenName: string;
    totalKots: number;
    avgPrepTimeMinutes: number;
    avgCookTimeMinutes: number;
    completedKots: number;
    cancelledKots: number;
  }[];
  bottleneckItems: {
    itemName: string;
    totalOrdered: number;
    avgPrepTimeMinutes: number;
    kitchenName: string;
  }[];
  statusDistribution: {
    status: string;
    count: number;
    percentage: number;
  }[];
  hourlyVolume: {
    hour: number;
    kotCount: number;
    avgPrepTimeMinutes: number;
  }[];
  tableTurnover: {
    tableName: string;
    orderCount: number;
    revenue: number;
    avgOrderValue: number;
    turnoverRate: number;
    avgSeatingMinutes: number;
  }[];
  avgTableTurnoverRate: number;
  avgSeatingDurationMinutes: number;
}

const KitchenPerformance: React.FC<KitchenPerformanceProps> = ({
  vendorId,
  startDate,
  endDate
}) => {
  const { theme } = useTheme();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [data, setData] = useState<KitchenPerformanceData | null>(null);

  useEffect(() => {
    fetchData();
  }, [vendorId, startDate, endDate]);

  const fetchData = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await api.get(
        `/api/vendors/${vendorId}/reports/kitchen-performance`,
        { params: { startDate, endDate } }
      );
      setData(response.data);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to load kitchen performance data.');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-12">
        <div className="flex flex-col items-center gap-4">
          <div
            className="w-12 h-12 border-4 border-t-transparent rounded-full animate-spin"
            style={{ borderColor: `${theme.primary} transparent ${theme.primary} ${theme.primary}` }}
          />
          <p style={{ color: theme.text }}>Loading kitchen performance...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="glassmorphism-card p-6 rounded-xl border" style={{ backgroundColor: theme.components.card.background, borderColor: theme.danger }}>
        <div className="flex items-center gap-3">
          <XCircle size={24} style={{ color: theme.danger }} />
          <div>
            <h3 className="font-semibold" style={{ color: theme.danger }}>Error</h3>
            <p style={{ color: theme.text }}>{error}</p>
          </div>
        </div>
        <button onClick={fetchData} className="mt-4 px-4 py-2 rounded-lg" style={{ backgroundColor: theme.button.primary.bg, color: theme.button.primary.text }}>
          Retry
        </button>
      </div>
    );
  }

  if (!data) return null;

  const MetricCard: React.FC<{ title: string; value: string | number; icon: React.ReactNode; color: string; suffix?: string }> = ({ title, value, icon, color, suffix }) => (
    <div className="glassmorphism-card p-5 rounded-xl border" style={{ backgroundColor: theme.components.card.background, borderColor: theme.components.card.border }}>
      <div className="flex items-start justify-between">
        <div>
          <p className="text-sm font-medium opacity-70" style={{ color: theme.text }}>{title}</p>
          <div className="flex items-baseline gap-1 mt-2">
            <h3 className="text-xl sm:text-2xl md:text-3xl font-bold" style={{ color: theme.text }}>{value}</h3>
            {suffix && <span className="text-sm opacity-60" style={{ color: theme.text }}>{suffix}</span>}
          </div>
        </div>
        <div className="p-3 rounded-lg" style={{ backgroundColor: color + '20', color }}>{icon}</div>
      </div>
    </div>
  );

  const maxHourlyCount = data.hourlyVolume ? Math.max(...data.hourlyVolume.map(h => h.kotCount), 1) : 1;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="glassmorphism-card p-4 rounded-xl border" style={{ backgroundColor: theme.components.card.background, borderColor: theme.components.card.border }}>
        <div className="flex items-center gap-3">
          <ChefHat size={28} style={{ color: theme.primary }} />
          <div>
            <h2 className="text-2xl font-bold" style={{ color: theme.text }}>Kitchen Performance & Table Turnover</h2>
            <p className="text-sm opacity-70" style={{ color: theme.text }}>{startDate} to {endDate}</p>
          </div>
        </div>
      </div>

      {/* KOT Timing Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <MetricCard title="Avg Prep Time" value={(data.avgPrepTimeMinutes ?? 0).toFixed(1)} icon={<Timer size={24} />} color={theme.primary} suffix="min" />
        <MetricCard title="Avg Ack Time" value={(data.avgAckTimeMinutes ?? 0).toFixed(1)} icon={<Eye size={24} />} color={theme.accent} suffix="min" />
        <MetricCard title="Avg Cook Time" value={(data.avgCookTimeMinutes ?? 0).toFixed(1)} icon={<Flame size={24} />} color={theme.warning} suffix="min" />
        <MetricCard title="Avg Service Time" value={(data.avgServiceTimeMinutes ?? 0).toFixed(1)} icon={<ArrowUpDown size={24} />} color={theme.success} suffix="min" />
      </div>

      {/* KOT Summary */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <MetricCard title="Total KOTs" value={data.totalKots} icon={<BarChart3 size={24} />} color={theme.primary} />
        <MetricCard title="Completed KOTs" value={data.completedKots} icon={<CheckCircle size={24} />} color={theme.success} />
        <MetricCard title="Cancelled KOTs" value={data.cancelledKots} icon={<XCircle size={24} />} color={theme.danger} />
      </div>

      {/* Bottleneck Items */}
      {data.bottleneckItems && data.bottleneckItems.length > 0 && (
        <div className="glassmorphism-card p-6 rounded-xl border" style={{ backgroundColor: theme.components.card.background, borderColor: theme.components.card.border }}>
          <div className="flex items-center gap-2 mb-4">
            <AlertTriangle size={24} style={{ color: theme.warning }} />
            <h3 className="text-xl font-bold" style={{ color: theme.text }}>Bottleneck Items</h3>
            <span className="text-xs opacity-60 ml-2" style={{ color: theme.text }}>Longest avg prep time</span>
          </div>
          <div className="space-y-3">
            {data.bottleneckItems.map((item, idx) => (
              <div key={idx} className="flex items-center justify-between p-3 rounded-lg" style={{ backgroundColor: theme.gray[100] }}>
                <div className="flex items-center gap-3">
                  <span className="text-sm font-bold w-6 text-center" style={{ color: theme.gray[500] }}>#{idx + 1}</span>
                  <div>
                    <p className="font-medium text-sm" style={{ color: theme.text }}>{item.itemName}</p>
                    <p className="text-xs opacity-60" style={{ color: theme.text }}>{item.kitchenName} - {item.totalOrdered} ordered</p>
                  </div>
                </div>
                <div className="text-right">
                  <p className="text-lg font-bold" style={{ color: item.avgPrepTimeMinutes > 20 ? theme.danger : theme.warning }}>
                    {(item.avgPrepTimeMinutes ?? 0).toFixed(1)} min
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Kitchen Breakdown */}
      {data.kitchenBreakdown && data.kitchenBreakdown.length > 0 && (
        <div className="glassmorphism-card p-4 sm:p-6 rounded-xl border" style={{ backgroundColor: theme.components.card.background, borderColor: theme.components.card.border }}>
          <div className="flex items-center gap-2 mb-4">
            <ChefHat size={24} style={{ color: theme.primary }} />
            <h3 className="text-lg sm:text-xl font-bold" style={{ color: theme.text }}>Per-Kitchen Breakdown</h3>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {data.kitchenBreakdown.map((kitchen) => (
              <div key={kitchen.kitchenId} className="p-4 rounded-lg border" style={{ backgroundColor: theme.gray[50], borderColor: theme.gray[200] }}>
                <h4 className="font-semibold mb-3" style={{ color: theme.text }}>{kitchen.kitchenName}</h4>
                <div className="grid grid-cols-2 gap-2 text-sm">
                  <div>
                    <p className="opacity-60" style={{ color: theme.text }}>Total KOTs</p>
                    <p className="font-bold text-lg" style={{ color: theme.primary }}>{kitchen.totalKots}</p>
                  </div>
                  <div>
                    <p className="opacity-60" style={{ color: theme.text }}>Completed</p>
                    <p className="font-bold text-lg" style={{ color: theme.success }}>{kitchen.completedKots}</p>
                  </div>
                  <div>
                    <p className="opacity-60" style={{ color: theme.text }}>Avg Prep</p>
                    <p className="font-bold" style={{ color: theme.text }}>{(kitchen.avgPrepTimeMinutes ?? 0).toFixed(1)} min</p>
                  </div>
                  <div>
                    <p className="opacity-60" style={{ color: theme.text }}>Avg Cook</p>
                    <p className="font-bold" style={{ color: theme.text }}>{(kitchen.avgCookTimeMinutes ?? 0).toFixed(1)} min</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Hourly KOT Volume */}
      <div className="glassmorphism-card p-6 rounded-xl border" style={{ backgroundColor: theme.components.card.background, borderColor: theme.components.card.border }}>
        <div className="flex items-center gap-2 mb-4">
          <Clock size={24} style={{ color: theme.primary }} />
          <h3 className="text-xl font-bold" style={{ color: theme.text }}>Hourly KOT Volume</h3>
        </div>
        <div className="space-y-2">
          {data.hourlyVolume && data.hourlyVolume
            .filter(h => h.kotCount > 0)
            .sort((a, b) => b.kotCount - a.kotCount)
            .slice(0, 10)
            .map((hour) => (
              <div key={hour.hour}>
                <div className="flex justify-between text-sm mb-1">
                  <span style={{ color: theme.text }}>{hour.hour.toString().padStart(2, '0')}:00 - {(hour.hour + 1).toString().padStart(2, '0')}:00</span>
                  <span style={{ color: theme.text }}>{hour.kotCount} KOTs ({(hour.avgPrepTimeMinutes ?? 0).toFixed(0)} min avg)</span>
                </div>
                <div className="h-2 rounded-full overflow-hidden" style={{ backgroundColor: theme.gray[200] }}>
                  <div className="h-full rounded-full transition-all" style={{ width: `${(hour.kotCount / maxHourlyCount) * 100}%`, backgroundColor: theme.primary }} />
                </div>
              </div>
            ))}
        </div>
      </div>

      {/* Table Turnover Analysis */}
      <div className="glassmorphism-card p-6 rounded-xl border" style={{ backgroundColor: theme.components.card.background, borderColor: theme.components.card.border }}>
        <div className="flex items-center gap-2 mb-2">
          <Table2 size={24} style={{ color: theme.accent }} />
          <h3 className="text-xl font-bold" style={{ color: theme.text }}>Table Turnover Analysis</h3>
        </div>
        <div className="flex flex-wrap gap-3 sm:gap-6 mb-4 text-sm" style={{ color: theme.text }}>
          <span>Avg turnover: <strong>{(data.avgTableTurnoverRate ?? 0).toFixed(1)} orders/table/day</strong></span>
          <span>Avg seating: <strong>{(data.avgSeatingDurationMinutes ?? 0).toFixed(0)} min</strong></span>
        </div>

        {data.tableTurnover && data.tableTurnover.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr style={{ borderBottom: `2px solid ${theme.gray[200]}` }}>
                  <th className="text-left py-2 px-2 sm:px-3 font-semibold" style={{ color: theme.text }}>Table</th>
                  <th className="text-right py-2 px-2 sm:px-3 font-semibold" style={{ color: theme.text }}>Orders</th>
                  <th className="text-right py-2 px-2 sm:px-3 font-semibold" style={{ color: theme.text }}>Revenue</th>
                  <th className="text-right py-2 px-2 sm:px-3 font-semibold hidden sm:table-cell" style={{ color: theme.text }}>Avg Order</th>
                  <th className="text-right py-2 px-2 sm:px-3 font-semibold" style={{ color: theme.text }}>Turns/Day</th>
                  <th className="text-right py-2 px-2 sm:px-3 font-semibold hidden sm:table-cell" style={{ color: theme.text }}>Avg Seating</th>
                </tr>
              </thead>
              <tbody>
                {data.tableTurnover.slice(0, 15).map((table, idx) => (
                  <tr key={idx} style={{ borderBottom: `1px solid ${theme.gray[100]}` }}>
                    <td className="py-2 px-2 sm:px-3 font-medium" style={{ color: theme.text }}>{table.tableName}</td>
                    <td className="py-2 px-2 sm:px-3 text-right" style={{ color: theme.text }}>{table.orderCount}</td>
                    <td className="py-2 px-2 sm:px-3 text-right" style={{ color: theme.success }}>{formatCurrency(table.revenue)}</td>
                    <td className="py-2 px-2 sm:px-3 text-right hidden sm:table-cell" style={{ color: theme.text }}>{formatCurrency(table.avgOrderValue)}</td>
                    <td className="py-2 px-2 sm:px-3 text-right font-semibold" style={{ color: theme.primary }}>{(table.turnoverRate ?? 0).toFixed(1)}</td>
                    <td className="py-2 px-2 sm:px-3 text-right hidden sm:table-cell" style={{ color: theme.text }}>{(table.avgSeatingMinutes ?? 0).toFixed(0)} min</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <p className="text-center py-4" style={{ color: theme.gray[500] }}>No table data available for this period</p>
        )}
      </div>
    </div>
  );
};

export default KitchenPerformance;
